<template>
  <div v-if="assessmentMode">
    <h4 class="mb-3">
      {{ $t('RequestAssessment.assessment') }} {{ isEditing ? $t('RequestAssessment.revised') : '' }}
    </h4>

    <div class="mb-3">
      <b-button
        v-for="assessmentStatus in assessmentOptions"
        :key="assessmentStatus.name"
        class="status"
        size="sm"
        :disabled="busy"
        :variant="assessment === assessmentStatus.id ? assessmentStatus.variant : 'light'"
        @click="changeAssessmentStatus({ status: assessmentStatus.id })"
      >
        <i :class="assessmentStatus.icon" />
        {{ assessmentStatus.label }}
      </b-button>

      <b-button
        v-if="isEditing"
        key="cancel-editing-assessment"
        v-b-tooltip.hover
        class="status cancel-editing-assessment float-right"
        variant="light"
        size="sm"
        title="annuleren"
        @click="cancelEditingAssessment"
      >
        <i class="icon fa-fw fal fa-x" />
      </b-button>
    </div>

    <!-- assessment 4: reject request -->
    <template v-if="isAssessmentWithReasons">
      <b-form-select
        v-model="reason"
        class="mb-4"
        :options="reasonsForAssessment"
        :select-size="4"
        :disabled="busy"
      />
    </template>
    <template v-if="isAssessmentWithReasons && isCustomReason">
      <b-form-textarea
        v-model="remarks"
        :state="remarkIsValid"
        :disabled="busy"
        :placeholder="reasonPlaceholder"
        :trim="true"
        class="mb-3"
      />
    </template>

    <b-alert
      class="mb-3"
      dismissible
      :show="feedback !== ''"
      :variant="feedbackVariant"
      @dismissed="feedback = ''"
    >
      {{ feedback }}
    </b-alert>
    <!-- Show button only if assessment is defined -->
    <b-button
      v-if="assessment"
      class="mt-3 mb-3"
      :disabled="!allowConfirmation || busy"
      size="sm"
      :variant="assessment === 5 ? 'danger' : 'dark'"
      @click="confirmAssessmentStatus"
    >
      <template v-if="assessment === 5">
        <span> {{ $t('RequestAssessment.confirmRemove') }} </span>
      </template>
      <template v-else>
        <i class="icon fa-fw fal fa-envelope" />
        <span class="ml-2"> {{ $t('RequestAssessment.confirmAssessment') }} </span>
      </template>
    </b-button>
  </div>
</template>

<script>
/**
 * Assessment of a request is a uniform mechanism for all tenants
 *  What differs per tenant is the list of reasons for rejection
 *  Some rejection reasons allow for an explanation (see remark watcher for input validation)
 */

import { multilineUnicodeString, getInvalidCharacters } from '@/services/validation';

import { mapGetters, mapMutations } from 'vuex';

export default {
  name: 'Assessment',
  props: {
    request: {
      type: Object,
      required: true
    },
    assessmentMode: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      /**
       * Form state
       **/
      busy: false,
      feedback: '',
      feedbackVariant: 'danger',

      /**
       * Form input
       */
      assessment: null,
      reason: null,
      remarks: '',

      remarkIsValid: null
    };
  },
  computed: {
    ...mapGetters('requests', ['statusOptions', 'statusOptionById', 'reasonOptions']),

    status() {
      return this.statusOptionById({ id: this.request.status });
    },
    isEditing() {
      return this.request.editAssessment;
    },
    isOpen() {
      return this.status.id === 2 || this.isEditing;
    },
    isAccepted() {
      return this.status.id === 3;
    },

    /**
     * Assessment
     */
    assessmentOptions() {
      // status 2 = open for assessment
      return this.statusOptions.filter(option => option.id !== 2);
    },

    /**
     * Check whether all conditions are met
     */
    allowConfirmation() {
      // Only accept one of the available assessment options
      if (!this.assessmentOptions.find(option => option.id === this.assessment)) {
        return false;
      }

      if (
        this.isEditing &&
        this.assessment === this.request.status && // don't allow re-evaluations with same status
        this.request.status === 4 &&
        this.reason === this.request.assessment.reason // except if reason for rejection has changed
      ) {
        return false;
      }

      return !this.isAssessmentWithReasons || (this.isValidReason && (!this.isCustomReason || this.remarkIsValid));
    },
    isValidReason() {
      return !!this.reasonsForAssessment.find(reason => reason.value === this.reason);
    },
    /**
     * Filter the assessment reasons by the selected assessment
     */
    reasonsForAssessment() {
      return this.reasonOptions.filter(reason => reason.status !== 3 && (reason.status || null) === this.assessment);
    },
    /**
     * Does the assessment option have a list of reasons to further specify the assessment?
     */
    isAssessmentWithReasons() {
      return this.reasonsForAssessment.length;
    },
    isCustomReason() {
      let reason = this.reasonOptions.find(reason => reason.value === this.reason);
      return reason ? !!reason.custom : false;
    },
    reasonPlaceholder() {
      let reason = this.reasonOptions.find(reason => reason.value === this.reason);
      return reason?.placeholder?.[this.$i18n.locale] || reason?.placeholder || this.$i18n.t('RequestAssessment.explanation');
    }
  },
  watch: {
    remarks(remarks) {
      if (remarks === '') {
        this.remarkIsValid = null;
      } else {
        this.remarkIsValid = remarks.length <= 4000 && multilineUnicodeString(remarks);

        if (!multilineUnicodeString(remarks)) {
          this.feedback = `Uw invoer bevat karakters die omwille van veiligheid niet geaccepteerd worden: ${getInvalidCharacters(
            remarks
          )}`;
        } else if (remarks.length > 4000) {
          this.feedback = `De tekst is te lang! (${remarks.length}/4000 tekens)`;
        } else {
          this.feedback = '';
        }
      }
    }
  },
  methods: {
    ...mapMutations('requests', ['updateRequest', 'replaceRequest']),

    changeAssessmentStatus({ status }) {
      this.assessment = status === this.assessment ? null : status;
    },

    /**
     * Update the request
     */
    async confirmAssessmentStatus() {
      if (this.allowConfirmation && !this.busy) {
        this.busy = true;

        try {
          // fix: reason with id 0 is evaluated to false
          let reasonLabel = !Number.isNaN(this.reason)
            ? this.reasonOptions.filter(reason => reason.value === this.reason)
            : null;
          reasonLabel = reasonLabel && reasonLabel.length ? reasonLabel[0].short : '';

          const payload = {
            ref: this.request.ref,
            data: {
              isCorrection: this.request.editAssessment,
              assessment: this.assessment,
              assessmentLabel: this.statusOptionById({ id: this.assessment }).name,
              reason: this.isAssessmentWithReasons ? this.reason : null,
              reasonLabel: this.isAssessmentWithReasons ? reasonLabel : '',
              remarks: this.isAssessmentWithReasons && this.isCustomReason ? this.remarks : ''
            }
          };
          const token = await this.$auth.getTokenSilently();
          const api = await fetch('/api/assessment', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
          const response = await api.json();

          if (response.statusCode !== 200) {
            throw new Error('Something went wrong');
          }

          this.replaceRequest({
            request: response.data.record
          });

          this.$emit('assessmentMode', { mode: false });
        } catch (e) {
          console.log(e);
          this.feedbackVariant = 'danger';
          this.feedback = 'Er ging iets fout. Data niet opgeslagen.';
        }
        this.busy = false;
      }
    },
    cancelEditingAssessment() {
      this.$emit('assessmentMode', { mode: false });
    }
  }
};
</script>

<style lang="scss">
.cancel-editing-assessment {
  &:hover {
    cursor: pointer;
  }
}

.icon {
  font-size: 1rem;
}
</style>
